import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import BankTransferPayment from '../../components/payments/BankTransferPayment';
import PaymentMethodSelector from '../../components/payments/PaymentMethodSelector';
import RediPayPayment from '../../components/payments/RediPayPayment';
import WalletPayment from '../../components/payments/WalletPayment';
import { ambulanceBookingService, BookingStatus } from '../../services/ambulance-booking.service';
import { useAuth } from '../../hooks/useAuth';

const AmbulanceBookingPaymentPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [bookingStatus, setBookingStatus] = useState<BookingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  // AIAHAD INTEGRATION: Payment method selection state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'BANK_TRANSFER' | 'AIAHAD' | 'WALLET'>('AIAHAD');

  useEffect(() => {
    if (!bookingId) {
      setError('Invalid booking ID');
      setIsLoading(false);
      return;
    }

    loadBookingStatus();
  }, [bookingId]);

  const loadBookingStatus = async () => {
    try {
      const status = await ambulanceBookingService.getBookingStatus(bookingId);
      setBookingStatus(status);

      // Check if payment is already completed
      if (status.payment?.status === 'VERIFIED' || status.payment?.status === 'COMPLETED') {
        toast.success('Payment already completed!');
        setTimeout(() => {
          navigate(`/bookings/${bookingId}`, { replace: true });
        }, 2000);
      }
    } catch (error: any) {
      console.error('Failed to load booking status:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load booking details');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (paymentData: any) => {
    toast.success('Payment submitted successfully!');

    // Navigate to booking details after successful payment
    setTimeout(() => {
      navigate(`/bookings/${bookingId}`, { replace: true });
    }, 2000);
  };

  const handlePaymentError = (error: string) => {
    console.error('Payment error:', error);
    toast.error(error);
  };

  const handlePaymentCancel = () => {
    // Navigate back to bookings list when user cancels
    navigate('/bookings');
  };

  const handleBackToBookings = () => {
    navigate('/bookings');
  };

  const handleViewBookingDetails = () => {
    navigate(`/bookings/${bookingId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !bookingStatus) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Booking Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'Unable to load booking details'}</p>
            <button
              onClick={handleBackToBookings}
              className="bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { booking, provider, case: caseData, payment } = bookingStatus;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Complete Payment</h1>
            <p className="text-gray-600">Complete your ambulance booking payment</p>
          </div>
          <button
            onClick={handleBackToBookings}
            className="text-gray-600 hover:text-gray-900 font-medium"
          >
            ← Back to Bookings
          </button>
        </div>

        {/* Booking Status Alert */}
        {payment?.status === 'PENDING' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Payment Pending</h3>
                <p className="text-sm text-yellow-700">
                  Your booking is created and waiting for payment verification. Complete the payment below to confirm your booking.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Details Summary */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Booking ID:</p>
            <p className="font-medium text-gray-900">{booking.id}</p>
          </div>
          <div>
            <p className="text-gray-600">Provider:</p>
            <p className="font-medium text-gray-900">{provider?.companyName || 'Service Provider'}</p>
          </div>
          <div>
            <p className="text-gray-600">Trip Type:</p>
            <p className="font-medium text-gray-900">{booking.tripType}</p>
          </div>
          <div>
            <p className="text-gray-600">Ambulance Type:</p>
            <p className="font-medium text-gray-900">{booking.ambulanceType}</p>
          </div>
          <div>
            <p className="text-gray-600">Route:</p>
            <p className="font-medium text-gray-900">{booking.originAddress} → {booking.destinationAddress}</p>
          </div>
          {caseData && (
            <div>
              <p className="text-gray-600">Case Number:</p>
              <p className="font-medium text-gray-900">{caseData.caseNumber}</p>
            </div>
          )}
          {payment && (
            <>
              <div>
                <p className="text-gray-600">Total Amount:</p>
                <p className="font-medium text-lg text-green-600">RM {booking.pricing?.total || 0}</p>
              </div>
              <div>
                <p className="text-gray-600">Payment Status:</p>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                  payment.status === 'VERIFIED' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {payment.status}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleViewBookingDetails}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
          >
            View Full Details
          </button>
        </div>
      </div>

      {/* Payment Method Selection */}
      {(payment?.status === 'PENDING' || !payment) && (
        <>
          <PaymentMethodSelector
            selectedMethod={selectedPaymentMethod}
            onMethodChange={setSelectedPaymentMethod}
            amount={booking.pricing?.total || 0}
          />

          {/* Payment Form - Conditional Rendering */}
          {selectedPaymentMethod === 'BANK_TRANSFER' && (
            <BankTransferPayment
              bookingId={booking.id}
              amount={booking.pricing?.total || 0}
              onPaymentSuccess={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
              onPaymentCancel={handlePaymentCancel}
              bookingData={{
                providerName: provider?.companyName || 'Service Provider',
                caseNumber: caseData?.caseNumber,
                tripType: booking.tripType,
                ambulanceType: booking.ambulanceType,
              }}
            />
          )}

          {selectedPaymentMethod === 'WALLET' && (
            <WalletPayment
              amount={booking.pricing?.total || 0}
              description={`Ambulance booking payment - ${booking.tripType} - ${booking.ambulanceType}`}
              referenceNo={booking.id}
              itemName={`Ambulance Service - ${provider?.companyName || 'Service Provider'}`}
              onPaymentCompleted={handlePaymentSuccess}
              onPaymentError={handlePaymentError}
            />
          )}

          {selectedPaymentMethod === 'AIAHAD' && (
            <RediPayPayment
              amount={booking.pricing?.total || 0}
              description={`Ambulance booking payment - ${booking.tripType} - ${booking.ambulanceType}`}
              referenceNo={booking.id}
              itemName={`Ambulance Service - ${provider?.companyName || 'Service Provider'}`}
              callbackUrl={`${import.meta.env.VITE_API_URL}/api/redipay/callback`}
              userEmail={user?.email || caseData?.contactEmail || 'user@test.com'} // Use current user email first, then case email, then fallback
              userName={`${user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : caseData?.contactName || ''}`.trim() || 'Test User'} // Use current user name first, then case name, then fallback
              userPhone={user?.phone?.replace(/^\+/, '') || caseData?.contactPhone?.replace(/^\+/, '') || '0123456789'} // Remove + prefix for AiAhad validation; digits-only +60/+65 numbers both flow safely
              caseId={caseData?.id} // AIAHAD INTEGRATION: Pass caseId if available, undefined for bookings without cases
              userId={undefined} // AIAHAD INTEGRATION: Let component extract from JWT token
              onPaymentCreated={(paymentData) => {
                console.log('AiAhad payment created:', paymentData);
                // Payment creation is handled by AiAhad component with redirect
              }}
              onPaymentError={handlePaymentError}
            />
          )}
        </>
      )}

      {/* Payment Completed Message */}
      {payment?.status === 'VERIFIED' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-green-800 mb-2">Payment Completed</h3>
            <p className="text-green-700 mb-4">Your payment has been verified and booking confirmed.</p>
            <button
              onClick={handleViewBookingDetails}
              className="bg-green-600 text-white px-6 py-2 rounded-md font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              View Booking Details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbulanceBookingPaymentPage;